import { BadgeCheck, CheckCircle2, ChevronRight, Info, Star, Store, } from "lucide-react";
import { useEffect, useState, } from "react";
import { Navigate, useNavigate, useParams, } from "react-router-dom";
import { sellerFeedbackReasons, } from "./SellerFeedbackData";
import { SellerFeedbackReasonId, } from "./SellerFeedbackTypes";
import "./SellerFeedbackPage.css";
import { useCart } from "../../../context/CartContext";
import { useAuth } from "../../../context/AuthContext";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { RootState } from "../../../store";
import { fetchOrderById } from "../../../Redux/custmoer_orders/slice";
import {
    checkMySellerReview,
    createStoreReview,
    updateStoreReview,
} from "../../../Redux/storeReview/slice";
import { formatLongOrderDate } from "../../../user/pages/orders/ordersAdapters";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";

const SellerFeedbackPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { cartItems } = useCart();
    const { isAuthenticated } = useAuth();

    const cartCount = cartItems.reduce(
        (total, cartItem) => total + cartItem.quantity,
        0
    );

    const { orderId = "" } = useParams<{ orderId: string }>();
    const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [selectedReasons, setSelectedReasons] = useState<SellerFeedbackReasonId[]>([]);
    const [comment, setComment] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const [existingReviewId, setExistingReviewId] = useState<string | null>(null);

    const { currentOrder, loading: orderLoading, error: orderError } =
        useAppSelector((state: RootState) => state.customerOrders);
    const { loading: reviewLoading, mySellerReview } =
        useAppSelector((state: RootState) => state.storeReview);

    const order = currentOrder;
    const item = order?.productDetails?.[0];
    const sellerId =
        item?.sellerId || order?.sellerDetails?.sellerId || "";
    const sellerName =
        item?.sellerName || order?.sellerDetails?.sellerName || "Seller";

    useEffect(() => {
        if (orderId) {
            dispatch(fetchOrderById(orderId));
        }
    }, [dispatch, orderId]);

    useEffect(() => {
        if (sellerId && orderId) {
            dispatch(checkMySellerReview({ sellerId, orderId }));
        }
    }, [dispatch, sellerId, orderId]);

    useEffect(() => {
        if (mySellerReview) {
            setExistingReviewId(mySellerReview._id);
            setRating(mySellerReview.rating);
            setSelectedReasons(
                (mySellerReview.reasons || []) as SellerFeedbackReasonId[]
            );
            setComment(mySellerReview.review || "");
        }
    }, [mySellerReview]);

    const ratingLabel =
        rating === 1 ? "Very Poor" : rating === 2 ? "Poor"
            : rating === 3 ? "Neutral" : rating === 4 ? "Good" : rating === 5 ? "Excellent" : "Select a rating";

    const toggleReason = (reasonId: SellerFeedbackReasonId) => {
        setSelectedReasons((current) => current.includes(reasonId) ? current.filter((id) => id !== reasonId) : [...current, reasonId,]);
        setError("");
    };

    const handleSubmit = async () => {
        if (rating === 0) {
            setError("Please select a star rating before submitting.");
            return;
        }
        if (rating < 4 && selectedReasons.length === 0) {
            setError("Please select at least one reason for a rating below 4 stars.");
            return;
        }
        if (!sellerId || !orderId) {
            setError("Unable to identify the seller for this order.");
            return;
        }

        const review = comment.trim() || undefined;
        const resultAction = existingReviewId
            ? await dispatch(
                updateStoreReview({
                    id: existingReviewId,
                    rating,
                    reasons: selectedReasons,
                    review,
                })
            )
            : await dispatch(
                createStoreReview({
                    targetSellerId: sellerId,
                    orderId,
                    rating,
                    reasons: selectedReasons,
                    review,
                })
            );

        if (
            createStoreReview.fulfilled.match(resultAction) ||
            updateStoreReview.fulfilled.match(resultAction)
        ) {
            setError("");
            setSubmitted(true);
        } else {
            setError(
                (resultAction.payload as string) ||
                "Failed to submit feedback. Please try again."
            );
        }
    };

    const openProduct = () => {
        if (item?.productId) {
            navigate(`/DetailPage/${item.productId}`);
        }
    };

    const renderSellerFeedbackNavbar = () => (
        <>
            <div className="seller-feedback-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <div className="seller-feedback-mobile-navbar">
                <MobileDetailNavbar
                    cartCount={cartCount}
                    searchPlaceholder="Search Salar"
                    fallbackPath={orderId ? `/OrderDetails/${orderId}` : "/MyOrders"}
                />
            </div>
            <div className="seller-feedback-desktop-secondary-navbar">
                <HomeSecondaryNavbar
                    variant="detail"
                    browsingHistoryOpen={browsingHistoryOpen}
                    onBrowsingHistoryClick={() =>
                        setBrowsingHistoryOpen((current) => !current)
                    }
                />
            </div>
        </>
    );

    if (!isAuthenticated) {
        return <Navigate to="/SignIn" replace />;
    }

    if (orderLoading) {
        return (
            <>
                {renderSellerFeedbackNavbar()}
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                    <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </>
        );
    }

    if (orderError || !order) {
        return (
            <>
                {renderSellerFeedbackNavbar()}
                <main className="seller-feedback-page">
                    <div className="seller-feedback-shell">
                        <div className="seller-feedback-error">
                            <Info size={18} />
                            <span>{orderError || "Order not found."}</span>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    if (submitted) {
        return (
            <>
                {renderSellerFeedbackNavbar()}

                <main className="seller-feedback-page">
                    <div className="seller-feedback-shell">
                        <section className="seller-feedback-success">
                            <div className="seller-feedback-success-icon">
                                <CheckCircle2 size={42} />
                            </div>
                            <span> FEEDBACK SUBMITTED </span>
                            <h1> Thanks for sharing your seller experience </h1>
                            <p> Your feedback has been recorded for <strong>
                                {sellerName}
                            </strong>. It helps improve marketplace service quality and gives future buyers more useful seller information.
                            </p>
                            <div>
                                <button type="button" onClick={() => navigate(`/OrderDetails/${orderId}`)} > Back to Order Details </button>
                                <button type="button" onClick={() => navigate(`/seller/${sellerId}`)} > View Seller Profile </button>
                            </div>
                        </section>
                    </div>
                </main>
            </>
        );
    }
    return (
        <>
            {renderSellerFeedbackNavbar()}

            <main className="seller-feedback-page">
                <div className="seller-feedback-shell">
                    <div className="seller-feedback-breadcrumb">
                        <button type="button" onClick={() => navigate("/MyOrders")}>
                            Your Orders
                        </button>
                        <ChevronRight size={15} />
                        <button type="button" onClick={() => navigate(`/OrderDetails/${orderId}`)}>
                            Order Details
                        </button>
                        <ChevronRight size={15} />
                        <strong>Seller Feedback</strong>
                    </div>
                    <section className="seller-feedback-hero">
                        <div>
                            <span> SELLER EXPERIENCE </span>
                            <h1> {existingReviewId ? "Update Seller Feedback" : "Leave Seller Feedback"} </h1>
                            <p> Rate the service you received from the seller for this order. Seller feedback is about fulfilment, communication and order handling—not whether you personally liked the product.</p>
                        </div>
                        <div className="seller-feedback-order-ref">
                            <span> ORDER ID </span>
                            <strong> {order.orderId} </strong>
                            <small> {formatLongOrderDate(order.orderDate)} </small>
                        </div>
                    </section>
                    <div className="seller-feedback-layout">
                        <section className="seller-feedback-main">
                            <article className="seller-feedback-card seller-context-card">
                                <div className="seller-context-product">
                                    {item?.productImage && (
                                        <button type="button" className="seller-context-image" onClick={openProduct}>
                                            <img src={item.productImage} alt={item.productName || "Product"} />
                                        </button>
                                    )}
                                    <div className="seller-context-copy">
                                        <button type="button" className="seller-context-product-link" onClick={openProduct}>
                                            <strong> {item?.productName || "Order item"}</strong>
                                            {item?.sku && <span> SKU: {item.sku}</span>}
                                        </button>
                                        {item?.quantity && (
                                            <small> Qty {item.quantity}</small>
                                        )}
                                    </div>
                                </div>
                                <div className="seller-context-store">
                                    <div className="seller-context-store-icon">
                                        <Store size={22} />
                                    </div>
                                    <div>
                                        <span> SELLER </span>
                                        <button type="button" onClick={() => sellerId && navigate(`/seller/${sellerId}`)}>
                                            <strong> {sellerName}</strong>
                                            <BadgeCheck size={16} />
                                        </button>
                                        <p> Verified marketplace seller </p>
                                    </div>
                                </div>
                            </article>
                            <article className="seller-feedback-card seller-rating-card">
                                <div className="seller-feedback-section-head">
                                    <div>
                                        <h2> How was your experience with this seller? </h2>
                                        <p> Choose the rating that best reflects the seller's service for this order. </p>
                                    </div>
                                </div>
                                <div className="seller-rating-bar">
                                    <div className="seller-rating-stars">
                                        {[1, 2, 3, 4, 5,].map(
                                            (star) => (
                                                <button type="button" key={star}
                                                    className={star <= rating ? "selected" : ""}
                                                    onClick={() => { setRating(star); setError(""); }}
                                                    aria-label={`${star} star rating`}>
                                                    <Star size={27} fill={star <= rating ? "currentColor" : "none"} />
                                                </button>
                                            )
                                        )}
                                    </div>

                                    <div className="seller-rating-result">
                                        <span> YOUR RATING </span>
                                        <strong> {ratingLabel} </strong>
                                        <small> {rating > 0 ? `${rating} of 5 stars` : "Tap a star to rate the seller"} </small>
                                    </div>
                                </div>

                                <div className="seller-rating-scale">
                                    <span> <strong> 1–2 </strong> Negative </span>
                                    <span> <strong> 3 </strong> Neutral </span>
                                    <span> <strong> 4–5 </strong> Positive </span>
                                </div>
                            </article>
                            <article className="seller-feedback-card seller-reasons-card">
                                <div className="seller-feedback-section-head">
                                    <div>
                                        <h2> What shaped your rating? </h2>
                                        <p> Select the seller-service areas that best match your experience. For ratings below 4 stars, choosing at least one reason is required. </p>
                                    </div>
                                </div>
                                <div className="seller-reason-grid">
                                    {sellerFeedbackReasons.map(
                                        (reason) => {
                                            const active = selectedReasons.includes(reason.id);
                                            return (
                                                <button type="button" key={reason.id} className={active ? "active" : ""} onClick={() => toggleReason(reason.id)}>
                                                    <span className="seller-reason-check">
                                                        {active && (<CheckCircle2 size={17} />)}
                                                    </span>
                                                    <span>
                                                        <strong> {reason.label} </strong>
                                                        <small> {reason.description} </small>
                                                    </span>
                                                </button>
                                            );
                                        }
                                    )}
                                </div>
                            </article>
                            <article className="seller-feedback-card seller-comment-card">
                                <div className="seller-feedback-section-head">
                                    <div>
                                        <h2> Add a comment </h2>
                                        <p> Optional. Share a short seller-service note that would be useful to other customers. </p>
                                    </div>
                                </div>
                                <label className="seller-feedback-textarea">
                                    <textarea value={comment} maxLength={500} onChange={(event) => setComment(event.target.value)}
                                        placeholder="Example: Dispatch was quick, packaging was secure and the seller responded clearly when I had a delivery question." />
                                    <span> {comment.length} / 500 </span>
                                </label>
                                <div className="seller-feedback-guidance">
                                    <Info size={18} />
                                    <p> Keep feedback focused on the seller's service. Product quality, fit, features or personal product preference belong in a product review. </p>
                                </div>
                            </article>
                            {error && (
                                <div className="seller-feedback-error">
                                    <Info size={18} />
                                    <span> {error} </span>
                                </div>
                            )}
                            <div className="seller-feedback-submit-row">
                                <button type="button" className="seller-feedback-cancel"
                                    onClick={() => navigate(`/OrderDetails/${orderId}`)}>
                                    Cancel
                                </button>
                                <button type="button" className="seller-feedback-submit" onClick={handleSubmit} disabled={reviewLoading}>
                                    {reviewLoading ? "Submitting..." : existingReviewId ? "Update Feedback" : "Submit Seller Feedback"}
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            </main>
        </>
    );
};
export default SellerFeedbackPage;
